import { clientArtifactRefusalCopy } from "../data/client-artifact-contract";

const PDF_FAILURE = clientArtifactRefusalCopy["R-06"]("Proof of service");
const ascii = (text: string): Uint8Array<ArrayBuffer> => new TextEncoder().encode(text);
interface PdfImage { bytes: Uint8Array<ArrayBuffer>; width: number; height: number }

/** A deliberately small PDF image container: no metadata, links, JavaScript, or external resources. */
export function encodeClientArtifactPdf(images: readonly PdfImage[]): Blob {
  if (images.length === 0) throw new Error(PDF_FAILURE);
  const parts: Uint8Array<ArrayBuffer>[] = [];
  const offsets: number[] = [0];
  let length = 0;
  const append = (value: string | Uint8Array<ArrayBuffer>) => {
    const bytes = typeof value === "string" ? ascii(value) : value;
    parts.push(bytes);
    length += bytes.byteLength;
  };
  const object = (number: number, content: string) => {
    offsets[number] = length;
    append(`${number} 0 obj\n${content}\nendobj\n`);
  };
  append("%PDF-1.4\n");
  append(new Uint8Array([37, 226, 227, 207, 211, 10]));
  object(1, "<< /Type /Catalog /Pages 2 0 R >>");
  object(2, `<< /Type /Pages /Count ${images.length} /Kids [${images.map((_, index) => `${3 + index * 3} 0 R`).join(" ")}] >>`);
  images.forEach((image, index) => {
    if (image.width !== 1632 || image.height !== 2112 || image.bytes[0] !== 0xff || image.bytes[1] !== 0xd8) throw new Error(PDF_FAILURE);
    const page = 3 + index * 3;
    const raster = page + 1;
    const content = page + 2;
    object(page, `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Resources << /XObject << /Sheet ${raster} 0 R >> >> /Contents ${content} 0 R >>`);
    offsets[raster] = length;
    append(`${raster} 0 obj\n<< /Type /XObject /Subtype /Image /Width ${image.width} /Height ${image.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${image.bytes.byteLength} >>\nstream\n`);
    append(image.bytes);
    append("\nendstream\nendobj\n");
    const commands = "q\n612 0 0 792 0 0 cm\n/Sheet Do\nQ\n";
    object(content, `<< /Length ${ascii(commands).byteLength} >>\nstream\n${commands}endstream`);
  });
  const xref = length;
  append(`xref\n0 ${offsets.length}\n0000000000 65535 f \n`);
  for (let index = 1; index < offsets.length; index += 1) append(`${String(offsets[index]).padStart(10, "0")} 00000 n \n`);
  append(`trailer\n<< /Size ${offsets.length} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`);
  return new Blob(parts, { type: "application/pdf" });
}

function checkCurrent(isCurrent: () => boolean): void {
  if (!isCurrent()) throw new Error(PDF_FAILURE);
}

function canvasJpeg(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => canvas.toBlob((blob) => {
    if (blob?.type === "image/jpeg") resolve(blob);
    else reject(new Error(PDF_FAILURE));
  }, "image/jpeg", 1));
}

const CAPTURE_DEADLINE_MS = 120_000;

/** Bound the whole capture. A hung browser callback must not keep the export busy forever. */
export function generateClientArtifactPdf(root: HTMLElement, isCurrent: () => boolean): Promise<Blob> {
  return new Promise((resolve, reject) => {
    let active = true;
    const deadline = performance.now() + CAPTURE_DEADLINE_MS;
    let currentCanvas: HTMLCanvasElement | null = null;
    const releaseRaster = () => {
      const canvas = currentCanvas;
      currentCanvas = null;
      if (canvas) {
        // Pixel release is best effort; a browser cleanup error must not prevent refusal.
        try { canvas.width = 0; canvas.height = 0; } catch { /* No artifact may be released. */ }
      }
    };
    const timer = globalThis.setTimeout(() => {
      active = false;
      releaseRaster();
      reject(new Error(PDF_FAILURE));
    }, CAPTURE_DEADLINE_MS);
    void captureRetainedPages(root, () => active && performance.now() < deadline && isCurrent(), canvas => { currentCanvas = canvas; }).then(
      pdf => {
        if (!active) return;
        active = false;
        globalThis.clearTimeout(timer);
        releaseRaster();
        if (performance.now() >= deadline) { reject(new Error(PDF_FAILURE)); return; }
        resolve(pdf);
      },
      () => {
        if (!active) return;
        active = false;
        globalThis.clearTimeout(timer);
        releaseRaster();
        reject(new Error(PDF_FAILURE));
      },
    );
  });
}

/** Capture exactly the retained measured pages, with caller/generation checks at every async boundary. */
async function captureRetainedPages(
  root: HTMLElement,
  isCurrent: () => boolean,
  onCanvas: (canvas: HTMLCanvasElement | null) => void,
): Promise<Blob> {
  checkCurrent(isCurrent);
  const pageSet = () => Array.from(root.querySelectorAll<HTMLElement>(".pos-paper:not(.pos-sheet-measure)"));
  const pages = pageSet();
  if (pages.length === 0) throw new Error(PDF_FAILURE);
  const checkPages = () => {
    checkCurrent(isCurrent);
    const currentPages = pageSet();
    if (!root.isConnected || currentPages.length !== pages.length
      || currentPages.some((page, index) => page !== pages[index])) throw new Error(PDF_FAILURE);
  };
  checkPages();
  const { default: html2canvas } = await import("html2canvas-pro");
  checkPages();
  const images: PdfImage[] = [];
  for (const page of pages) {
    checkPages();
    const canvas = await html2canvas(page, {
      allowTaint: false, backgroundColor: "#ffffff", height: 1056, width: 816,
      scale: 2, logging: false, useCORS: false, imageTimeout: 0,
      scrollX: 0, scrollY: 0,
      onclone: (_document, clonedPage) => {
        // The viewport shell scrolls on screen. Its clipping must not crop later paper pages.
        let ancestor = clonedPage.parentElement;
        while (ancestor) {
          ancestor.style.setProperty("overflow", "visible", "important");
          ancestor.style.setProperty("height", "auto", "important");
          ancestor.style.setProperty("max-height", "none", "important");
          ancestor.style.setProperty("position", "static", "important");
          ancestor.style.setProperty("transform", "none", "important");
          ancestor = ancestor.parentElement;
        }
        clonedPage.style.margin = "0";
        clonedPage.style.boxShadow = "none";
        clonedPage.querySelectorAll<HTMLElement>(".pos-paper-template i").forEach((chip) => { chip.style.backgroundColor = "#aaa"; });
      },
    });
    onCanvas(canvas);
    try {
      checkPages();
      const jpeg = await canvasJpeg(canvas);
      checkPages();
      const bytes = new Uint8Array(await jpeg.arrayBuffer());
      checkPages();
      images.push({ bytes, width: canvas.width, height: canvas.height });
    } finally {
      canvas.width = 0;
      canvas.height = 0;
      onCanvas(null);
    }
  }
  checkPages();
  return encodeClientArtifactPdf(images);
}
